'use client';
import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export interface UserAnalytics {
  id: string;
  userId: string;
  categoryId: string;
  categoryName: string;
  correctAnswers: number;
  totalAnswers: number;
  accuracy: number;
}

export const analyticsService = {
  async getUserAnalytics(userId: string): Promise<UserAnalytics[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('user_analytics')
        .select('*, categories(name)')
        .eq('user_id', userId)
        .order('category_id', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((a: any) => ({
        id: a.id,
        userId: a.user_id,
        categoryId: a.category_id,
        categoryName: a.categories?.name || '',
        correctAnswers: a.correct_answers,
        totalAnswers: a.total_answers,
        accuracy: parseFloat(a.accuracy) || 0,
      }));
    } catch (error: any) {
      if (isSchemaError(error)) {
        throw error;
      }
      return [];
    }
  },

  async updateAnalytics(userId: string, categoryId: string, isCorrect: boolean): Promise<boolean> {
    const supabase = createClient();
    try {
      // Get current analytics
      const { data: current } = await supabase
        .from('user_analytics')
        .select('*')
        .eq('user_id', userId)
        .eq('category_id', categoryId)
        .single();

      if (current) {
        // Update existing record
        const newCorrect = current.correct_answers + (isCorrect ? 1 : 0);
        const newTotal = current.total_answers + 1;
        const newAccuracy = ((newCorrect / newTotal) * 100).toFixed(2);

        const { error } = await supabase
          .from('user_analytics')
          .update({
            correct_answers: newCorrect,
            total_answers: newTotal,
            accuracy: parseFloat(newAccuracy),
          })
          .eq('user_id', userId)
          .eq('category_id', categoryId);

        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error:', error.message);
            throw error;
          } else {
            console.log('Data error:', error.message);
            return false;
          }
        }
      } else {
        // Create new record
        const accuracy = isCorrect ? 100 : 0;
        const { error } = await supabase.from('user_analytics').insert({
          user_id: userId,
          category_id: categoryId,
          correct_answers: isCorrect ? 1 : 0,
          total_answers: 1,
          accuracy: accuracy,
        });

        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error:', error.message);
            throw error;
          } else {
            console.log('Data error:', error.message);
            return false;
          }
        }
      }

      return true;
    } catch (error: any) {
      if (isSchemaError(error)) {
        throw error;
      }
      return false;
    }
  },
};